import { CONTENT_WIDTH, MARGIN_LEFT, bottomMarginItemGlobal } from "../../utils/pdfConstants";

export function renderTekniikkatauluBlock({
  type = "Rakennetekniikka",
  ptsContext,
  content = [],
  useDemo = false,
  fontSize = 10,
  contentWidth = CONTENT_WIDTH,
  hideKuntoluokka = false,
  showAlvZero = false,
  hide11thYear = false,
} = {}) {
  if (!ptsContext) throw new Error("ptsContext is required");

  const tableData = {
    "Rakennetekniikka": ptsContext.tekniikkaData,
    "LVI Järjestelmät": ptsContext.lviData,
    "Sähköjärjestelmät": ptsContext.sahkoData
  }[type] || [];

  const totalsByType = {
    "Rakennetekniikka": ptsContext.tekniikkaYhteensa,
    "LVI Järjestelmät": ptsContext.lviYhteensa,
    "Sähköjärjestelmät": ptsContext.sahkoYhteensa
  }[type] || [];

  // Display-only truncation — the underlying 11-value arrays in tableData/
  // totalsByType are never touched, only sliced at the point each is drawn.
  const allYears = ptsContext.years || Array.from({ length: 11 }, (_, i) => 2023 + i);
  const years = hide11thYear ? allYears.slice(0, 10) : allYears;
  const smallFontSize = fontSize * 0.85;
  const yearsFontSize = fontSize * 0.75;

  const cellPadH = 4; // matches layout paddingLeft/paddingRight
  const firstColW = 155;
  const klColW = 24; // must fit "KL3" text (~15pt) + 8pt padding
  const numFixedCols = hideKuntoluokka ? 1 : 2;
  const yearW = (contentWidth - firstColW - (hideKuntoluokka ? 0 : klColW) - (numFixedCols + years.length) * cellPadH * 2) / years.length;

  const getKLColor = (kl) => {
    switch (kl) {
      case "KL1":
      case "KL2": return "#ba3b46";
      case "KL3": return "#d0c407";
      case "KL4":
      case "KL5": return "#04aa00";
      default: return "black";
    }
  };
  const renderCellValue = (v) => {
    // Treat 0, null, undefined as empty
    if (v === 0 || v === null || v === undefined || v=== '0') return "";
    return String(v); // convert everything else to string
  };

  const rows = [];
  const alvLabel = showAlvZero
    ? `Kustannusarvio (x 1000€) Kustannustaso ${new Date().getFullYear()} ALV 0%`
    : `Kustannusarvio (x 1000€) Kustannustaso ${new Date().getFullYear()} sis. Alv 25,5%`;

  // --- Super header row ---
  rows.push([
    { text: "", colSpan: numFixedCols, fillColor: "#04aa00" }, ...(hideKuntoluokka ? [] : [{}]),
    { text: alvLabel,
      colSpan: years.length,
      alignment: "right",
      bold: true,
      font: "Lato",
      fontSize: smallFontSize,
      color: "white",
      fillColor: "#04aa00",
      margin: [0, 2, 4, 2]
    },
    ...Array(years.length - 1).fill({})
  ]);

  // --- Header row ---
  rows.push([
    { text: type, style: "tableHeader", colSpan: numFixedCols, bold: true, alignment: "left", font: "Lato", fontSize: smallFontSize, fillColor: "#04aa00", color:'white' },
    ...(hideKuntoluokka ? [] : [{}]),
    ...years.map(y => ({
      text: String(y),
      style: "tableHeader",
      alignment: "center",
      fillColor: "#04aa00",
      color: "white",
      bold: true,
      font: "Lato",
      fontSize: yearsFontSize,
    }))
  ]);

  // --- Table body ---
  tableData.forEach(section => {
    const klNumbers = section.items.map(it => parseInt((it.kl || "").replace("KL", ""))).filter(n => !isNaN(n));
    const avgKL = klNumbers.length === 0 ? "" : "KL" + Math.round(klNumbers.reduce((a,b) => a+b, 0)/klNumbers.length);

    // Section header
    rows.push([
      { text: section.name, bold: true, colSpan: 1, fillColor: "#f0f0f0", font: "Lato", fontSize: smallFontSize, noWrap: false },
      ...(hideKuntoluokka ? [] : [{ text: avgKL, alignment: "center", bold: true, color: avgKL ? getKLColor(avgKL) : "inherit", font: "Lato", fillColor: "#f0f0f0", fontSize: smallFontSize }]),
      ...years.map(() => ({ text: "", fillColor: "#f0f0f0", font: "Lato", fontSize: smallFontSize }))
    ]);

    // Section items
    section.items.forEach(item => {
      const label = (item.label || "").trim();
      if (label.length < 2) return; // skip empty labels

      rows.push([
        { text: label, alignment: "left", font: "Lato", fontSize: smallFontSize, noWrap: false, margin: [6,0,0,0] },
        ...(hideKuntoluokka ? [] : [{ text: "", fontSize: smallFontSize }]), // KL empty
        ...item.values.slice(0, years.length).map(v => ({
          text: renderCellValue(v),
          alignment: "center",
          font: "Lato",
          fontSize: smallFontSize,
        }))
      ]);
    });
  });

  // Totals row
  rows.push([
    { text: "YHTEENSÄ", bold: true, color: "white", fillColor: "#04aa00", alignment: "left", font: "Lato", fontSize: smallFontSize },
    ...(hideKuntoluokka ? [] : [{ text: "", fillColor: "#04aa00" }]),
    ...totalsByType.slice(0, years.length).map(sum => ({
      text: renderCellValue(sum),
      bold: true,
      color: "white",
      fillColor: "#04aa00",
      alignment: "center",
      font: "Lato",
      fontSize: smallFontSize,
    }))
  ]);

  // --- Push table ---
content.push({
  table: {
    widths: [firstColW, ...(hideKuntoluokka ? [] : [klColW]), ...Array(years.length).fill(yearW)],
    body: rows
  },
  layout: {
    hLineWidth: () => 0.0,
    vLineWidth: () => 0.0,
    hLineColor: () => "#bbb",
    vLineColor: () => "#bbb",
    paddingLeft: () => 4,
    paddingRight: () => 4,
    paddingTop: () => 4,
    paddingBottom: () => 4,
  },
  margin: [0, bottomMarginItemGlobal, 0, 0]
});
}